import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DatePicker, Table } from "antd";
import Card from "components/card";
import dayjs from "dayjs";
import { 
	DATE_FORMAT_MDY, 
	TIME_FORMAT_12HM,
	TIME_FORMAT_24HM,
	ROLE_EMPLOYEE, 
} from "variables/constants";
import Crudoption from "components/dropdown/crudoption";
import { getTimecardUpdateRequestList,
		 approveTimecardUpdateRequest,
		 rejectTimecardUpdateRequest,
		 doTimecardUpdateRequestDelete,
 } from "../../../../../redux/timecardupdaterequest/timecardUpdateRequestState";
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import SectionHeader from "components/section/SectionHeader";
import SearchFilter from "components/search/SearchFilter";
import { getTimecardUpdateRequestStatusList } from "../../../../../redux/optionlist/optionlistState";
import { API_URL } from "services/apiservice";

const TimecardUpdateRequestTable = ({timecardId, mode}) => {

	const dispatch = useDispatch();

	const recordList = useSelector((state) => state.timecardUpdateRequest.timecardUpdateRequestList);
	const authUser = useSelector((state) => state.auth.user);
	const filters = useSelector((state) => state.timecardUpdateRequest.timecardUpdateRequestList?.filters);
	const pagination = useSelector((state) => state.timecardUpdateRequest.timecardUpdateRequestList?.pagination);
	const approvalStatusList = useSelector((state) => state.optionlist.timecardUpdateRequestStatusList?.items);

	const doSearch = (filters) => dispatch(getTimecardUpdateRequestList(filters));

	const { RangePicker } = DatePicker;

	const handleDateChange = (dates, dateStrings) => {
		const newFilters = {
			paginate: pagination.per_page,
			page: 1
		};

		if (dates && dates[0] && dates[1]) {
			newFilters.start_date = dateStrings[0];
			newFilters.end_date = dateStrings[1];
		} 

		if (currentSearchValue) {
			newFilters.keyword = currentSearchValue;
		}

		doSearch(newFilters);
	};

	const [currentSearchValue, setCurrentSearchValue] = useState('');
	const [dateRangeKey, setDateRangeKey] = useState(Date.now());
	const dateRangeValue = filters?.start_date && filters?.end_date 
		? [dayjs(filters.start_date), dayjs(filters.end_date)]
		: null;
		
	const handleSearch = (searchFilters) => {
		const searchValue = searchFilters.keyword || '';
		setCurrentSearchValue(searchValue);
		
		setDateRangeKey(Date.now());
		
		const newFilters = {
			paginate: pagination.per_page,
			page: 1
		};
		
		if (searchValue) {
			if (filters?.start_date && filters?.end_date) {
				newFilters.start_date = filters.start_date;
				newFilters.end_date = filters.end_date;
			}
			newFilters.keyword = searchValue;
		} 
		
		doSearch(newFilters);
	};	

	useEffect(() => {
		const params = {
			page: 1,
			paginate: 10,
			...(timecardId && { tur_timecard_id: timecardId }),
		};
		dispatch(getTimecardUpdateRequestList(params));
		dispatch(getTimecardUpdateRequestStatusList());
	}, [dispatch, timecardId]);

	const handleTableChange = (pagination, tableFilters, sorter) => {
		const newFilters = {
			...recordList.filters,
			...(timecardId && { tur_timecard_id: timecardId }),
			page: pagination.current,
			paginate: pagination.pageSize,
		};

		if (tableFilters?.tur_status_name?.length) {
			newFilters.status_id = tableFilters.tur_status_name.join(",");
		} else {
			delete newFilters.status_id;
		}

		if (sorter?.field) {
			newFilters.sortby = sorter.field;
			newFilters.sortorder = sorter.order === "ascend" ? "asc" : "desc";
		}

		dispatch(getTimecardUpdateRequestList(newFilters));
	};

	const columns = [
	...(mode !== "view"
		? [{
			title: "Employee Name",
			dataIndex: "tur_employee_name",
			key: "tur_employee_name",
			width: "15%",
			sorter: true,
		}]
		: []),
		{
			title: "Date",
			dataIndex: "tur_date",
			key: "tur_date",
			width: "10%",
			sorter: true,
			render: (date) =>
				date ? dayjs(date).format(DATE_FORMAT_MDY) : "-",
		},
		{
			title: (
				<>
					<div>Schedule /</div>
					<div>Requested Schedule</div>
				</>
			),
			key: "tur_schedule",
			width: "15%",
			render: (_, record) => {
				const scheduleInChanged =
					dayjs(record.tur_requested_schedule_time_in).format("HH:mm:ss") !==
					dayjs(record.tur_original_schedule_time_in).format("HH:mm:ss");
				const scheduleOutChanged =
					dayjs(record.tur_requested_schedule_time_out).format("HH:mm:ss") !==
					dayjs(record.tur_original_schedule_time_out).format("HH:mm:ss");

				return (
					<>
						<div>
							{record.tur_original_schedule_time_in
								? `${dayjs(record.tur_original_schedule_time_in).format(TIME_FORMAT_12HM)}
								- ${dayjs(record.tur_original_schedule_time_out).format(TIME_FORMAT_12HM)}`
								: record.tur_schedule_type_name ?? "-"}
						</div>

						<div>
							<span className={scheduleInChanged ? "text-blue-500" : ""}>
								{record.tur_requested_schedule_time_in
									? dayjs(record.tur_requested_schedule_time_in).format(TIME_FORMAT_12HM)
									: "-"}
							</span>
							{" - "}
							<span className={scheduleOutChanged ? "text-blue-500" : ""}>
								{record.tur_requested_schedule_time_out
									? dayjs(record.tur_requested_schedule_time_out).format(TIME_FORMAT_12HM)
									: "-"}
							</span>
						</div>
					</>
				);
			}
		},
		{
			title: (
				<>
					<div>Actual Time-in-out /</div>
					<div>Requested Time-in-out</div>
				</>
			),
			key: "tur_punch",
			width: "15%",
			render: (_, record) => {
				const punchInChanged =
					dayjs(record.tur_requested_punch_in).format("HH:mm:ss") !==
					dayjs(record.tur_original_punch_in).format("HH:mm:ss");
				const punchOutChanged =
					dayjs(record.tur_requested_punch_out).format("HH:mm:ss") !==
					dayjs(record.tur_original_punch_out).format("HH:mm:ss");

				return (
					<>
						<div>
							{ `${record.tur_original_punch_in
									? dayjs(record.tur_original_punch_in).format(TIME_FORMAT_12HM)
									: ""} - ${
									record.tur_original_punch_out
										? dayjs(record.tur_original_punch_out).format(TIME_FORMAT_12HM)
										: ""
								}`}
						</div>

						<div>
							<span className={punchInChanged ? "text-blue-500" : ""}>
								{record.tur_requested_punch_in
									? dayjs(record.tur_requested_punch_in).format(TIME_FORMAT_12HM)
									: "-"}
							</span>
							{" - "}
							<span className={punchOutChanged ? "text-blue-500" : ""}>
								{record.tur_requested_punch_out
									? dayjs(record.tur_requested_punch_out).format(TIME_FORMAT_12HM)
									: "-"}
							</span>
						</div>
					</>
				);
			}
		},
		{
			title: "Reason",
			dataIndex: "tur_reason",
			key: "tur_reason",
			width: "20%",
			render: (_, record) => (
				<div className="flex-1 gap-3">
					<div><span className="font-medium">Update Reason: </span>{record?.tur_reason}</div>
					{record?.tur_rejection_reason &&
						<div><span className="font-medium">Rejection Reason: </span>{record?.tur_rejection_reason}</div>
					}
				</div>
			),
		},
		{
			title: (
				<>
					<div>Status /</div>
					<div>Date Filed</div>
				</>
			),
			dataIndex: "tur_status_name",
			key: "tur_status_name",
			width: "10%",
			filters: approvalStatusList?.map((approvalStatus) => ({
				text: approvalStatus.label,
				value: approvalStatus.value
			})) || [],
			filteredValue: filters?.status_id
				? filters.status_id.split(",")
				: null,
			render: (tur_status_name, record) => (
				<>
					<div className={`status-indicator ${tur_status_name?.toLowerCase()}`}>
						{tur_status_name}
					</div>
					<div>{record.created_at ? dayjs(record.tur_created_at).format(`${DATE_FORMAT_MDY} ${TIME_FORMAT_24HM}`) : "-"}</div>
				</>
				
			),
		},
		{
			title: "Attachment",
			dataIndex: "tur_attachment_id",
			key: "tur_attachment_id",
			width: "10%",
			render: (tur_attachment_id) => (
				<div>
					{tur_attachment_id ? 
					<a href={`${API_URL}/preview/attachment/${tur_attachment_id}`} target="_blank" rel="noopener noreferrer">
						View
					</a> : "None"}
				</div>
			),
		},
		...(mode === "view" ? 	
			[{
				title: "Approved/ Rejected By",
				dataIndex: "approved_by_name",
				key: "approved_by_name",
				width: "10%",
				render: (approved_by_name) => (
					<div>
						{approved_by_name}
					</div>
				),
			}]: []
		),
				
		...(authUser?.user_level !== ROLE_EMPLOYEE
		? [{
			dataIndex: "tur_id",
			key: "tur_id",
			width: "10%",
			render: (tur_id, record) => (
				<Crudoption
					recordId={record.tur_id}
					allowView={false}
					allowEdit={false}
					allowDelete={false}
					approveHandler={(turId) =>
						dispatch(
							approveTimecardUpdateRequest({ tur_id: turId })
						)
					}
					rejectHandler={(turId, reason) =>
						dispatch(
							rejectTimecardUpdateRequest({
								tur_id: turId,
								tur_rejection_reason: reason,
							})
						)
					}
					confirmationReason={{
						enabled: true,
						label: "Reason for Rejection",
						placeholder: "Enter reason for rejecting this request...",
						required: true,
					}}

					additionalMenu={[
						{
							key: "approve",
							icon: <CheckOutlined />,
							label: "Approve",
							confirm: "approve",
							available:
								record.tur_status_name === "Pending" ? 1 : 0,
						},
						{
							key: "reject",
							icon: <CloseOutlined className="text-red-600" />,
							label: "Reject",
							confirm: "reject",
							available:
								record.tur_status_name === "Pending" ? 1 : 0,
						},
					]}
				/>
			),
		}]
		: []),
	];

	return (
		<Card extra="w-full h-full p-4">
			{mode === "view" &&
				<SectionHeader title="Timecard Update Request History"/>
			}
			<div className="h-full w-full overflow-x-auto">
				{mode !== "view" && 
					<div className="grid grid-cols-1 md:grid-cols-2 gap-2 items-center">
						{authUser?.user_level !== 5 && (
							<div className="p-1">
								<SearchFilter
									placeholder="Search by Employee Name"
									searchHandler={handleSearch}
									filters={filters}
									pagination={pagination}
								/>
							</div>
						)}

						<div className={`p-2 flex justify-end ${authUser?.user_level === 5 ? 'col-span-2' : 'col-span-1'}`}>
							<RangePicker
								key={dateRangeKey}
								className="h-9 w-full md:w-72 !rounded-lg input-field border"
								format="YYYY-MM-DD"
								onChange={handleDateChange}
								placeholder={['Start Date', 'End Date']}
								allowClear
								value={dateRangeValue}
							/>
						</div>
					</div>
				}
				<Table
					columns={columns}
					dataSource={recordList.items}
					rowKey="tur_id"
					loading={recordList.requesting}
					pagination={{
						current: recordList.pagination.current_page,
						pageSize: recordList.pagination.per_page,
						total: recordList.pagination.total,
					}}
					onChange={handleTableChange}
				/>
			</div>
		</Card>
	);
};

export default (TimecardUpdateRequestTable);